import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { peerExclusionKeys } from "@/lib/query";

/** The peer-derivation exclusion list — EPIC-056 WI-201. */

async function failure(res: Response, fallback: string): Promise<Error> {
  const json = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new Error(typeof json?.error === "string" && json.error ? json.error : fallback);
}

export interface PeerExcludedProject {
  id: string;
  nosProjectId: string;
  projectName: string;
  createdAt: string;
}

export function usePeerExclusions() {
  return useQuery({
    queryKey: peerExclusionKeys.all,
    queryFn: async (): Promise<PeerExcludedProject[]> => {
      const res = await fetch("/api/review-peer-exclusions");
      if (!res.ok) throw await failure(res, "Failed to load the exclusion list");
      return (await res.json()).data;
    },
  });
}

export interface NosProjectSearchResult {
  projectId: string;
  projectName: string;
}

/** Only searches once a real term is typed — the picker, not a browse list. */
export function useSearchNosProjects(search: string) {
  return useQuery({
    queryKey: peerExclusionKeys.search(search),
    queryFn: async (): Promise<NosProjectSearchResult[]> => {
      const res = await fetch(
        `/api/review-peer-exclusions/search?search=${encodeURIComponent(search)}`,
      );
      if (!res.ok) throw await failure(res, "Couldn't reach NOS to search projects");
      return (await res.json()).data;
    },
    enabled: search.trim().length > 0,
  });
}

export function useAddPeerExclusion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { nosProjectId: string; projectName: string }) => {
      const res = await fetch("/api/review-peer-exclusions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't exclude that project");
      return (await res.json()).data as { id: string };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peerExclusionKeys.all });
      toast.success("Project excluded from peer derivation");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useRemovePeerExclusion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/review-peer-exclusions/${id}`, { method: "DELETE" });
      if (!res.ok) throw await failure(res, "Couldn't remove that exclusion");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: peerExclusionKeys.all });
      toast.success("Exclusion removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
